import { isLocalStorageWpSiteControlEnabled } from '@shared/utils/localStorageDevFeatures';

import { isInIframe } from './iframe';

export enum AppMode {
  Web = 'web',
  SDK = 'sdk',
  WPSiteControl = 'wpSiteControl',
}

export interface AppModeSnapshot {
  mode: AppMode;
  isWeb: boolean;
  isSdk: boolean;
  isWpSiteControl: boolean;
  isEmbedded: boolean;
  isIframe: boolean;
  hasSdkQueryParam: boolean;
  usesAuth0: boolean;
  loginUrl: string;
}

export interface ResolveAppModeInput {
  origin: string;
  search: string;
  isIframe: boolean;
  wpSiteControlUrl?: string;
  wpSiteControlDevFlagEnabled: boolean;
}

function isWpSiteControlOrigin(origin: string, wpSiteControlUrl?: string) {
  return typeof wpSiteControlUrl === 'string' && wpSiteControlUrl.length > 0
    ? origin.includes(wpSiteControlUrl)
    : false;
}

/**
 * Internal core function exported for unit tests. Use `resolveAppMode` outside of this module
 */
export function resolveAppModeFromInput(input: ResolveAppModeInput): AppModeSnapshot {
  const searchParams = new URLSearchParams(input.search);
  const hasSdkQueryParam = searchParams.get('sdk') === 'true';
  const wpSiteControlEnabled =
    isWpSiteControlOrigin(input.origin, input.wpSiteControlUrl) ||
    input.wpSiteControlDevFlagEnabled;
  const mode: AppMode = wpSiteControlEnabled
    ? AppMode.WPSiteControl
    : input.isIframe || hasSdkQueryParam
      ? AppMode.SDK
      : AppMode.Web;
  const isWpSiteControl = mode === AppMode.WPSiteControl;
  const isSdk = mode === AppMode.SDK;
  const usesAuth0 = mode === AppMode.Web;

  return {
    mode,
    isWeb: mode === AppMode.Web,
    isSdk,
    isWpSiteControl,
    isEmbedded: isSdk || isWpSiteControl,
    isIframe: input.isIframe,
    hasSdkQueryParam,
    usesAuth0,
    loginUrl: isWpSiteControl ? '/login/wpsitecontrol' : '/login',
  };
}

export function resolveAppMode(search = window.location.search): AppModeSnapshot {
  return resolveAppModeFromInput({
    origin: window.location.origin,
    search,
    isIframe: isInIframe(),
    wpSiteControlUrl: import.meta.env.VITE_WP_SITE_CONTROL_URL,
    wpSiteControlDevFlagEnabled: isLocalStorageWpSiteControlEnabled(),
  });
}
